import React, { useState, useEffect } from "react";
import { Button, Modal, Row } from "antd";
import styled from "@emotion/styled";
import { CurrenciesList } from "common/Constants";

const PageIndicator = styled.div`
  background: #e2e2e8;
  border-radius: 10px;
  padding: 0px 15px;
  font-size: 17px;
  margin: 10px;
  cursor: pointer;
  color: #646a81;
  font-weight: 600;

  &.active {
    color: #6d87e6;
  }
`;

const ExpenseTitle = styled.div`
  margin: 10px;
  margin-left: 0 !important;
  font-size: 18px;
`;

const StyledContainer = styled.div`
  div {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .title {
    font-size: 15px;
    color: #636980;
  }

  .value {
    font-size: 17px;
    color: #000000d9;
  }
`;

const FileContainer = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #e2e2e8;
  color: blue;
  text-decoration: underline;
  aspect-ratio: 1/0.5;
  word-break: break-all !important;
`;

const Fileindex = styled.span`
  font-size: 18px;
`;

const BtnContainer = styled.div`
  background: #e2e2e8;
  border-radius: 50px;
  padding: 12px;
  font-size: 20px;
  margin: 10px;
  cursor: pointer;
  color: #646a81;
  font-weight: 900;

  .next-btn {
    margin: 0 10px;
  }

  .next-btn:hover {
    color: #6d87e6;
  }
`;

const PreviewModal = styled(Modal)`
  .ant-modal-content {
    max-width: 90vw;
    max-height: 90vh;
  }

  .ant-modal-body {
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 80vh;
    overflow: auto;
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const PDFContainer = styled.div`
  width: 100%;
  height: 80vh;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ViewBillProff = ({
  bills,
  selected,
  setselected,
  active,
  setActive,
  view,
  initialFileIndex = 0, // ✅ new prop
}) => {
  const [fileIndex, setFileIndex] = useState(initialFileIndex);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Reset fileIndex whenever the clicked file or active expense row changes
  useEffect(() => {
    setFileIndex(initialFileIndex);
  }, [initialFileIndex, active]);

  // ✅ Whenever fileIndex or selected changes while preview is open, update previewFile
  useEffect(() => {
    if (!previewVisible) return;
    const file = selected?.files?.[fileIndex];
    if (!file) return;
    const resolvedUrl = view
      ? file
      : file instanceof File
      ? URL.createObjectURL(file)
      : file;
    if (isPreviewableFile(resolvedUrl)) {
      setPreviewFile(resolvedUrl);
    } else {
      // navigated to a non-previewable file — close preview
      setPreviewVisible(false);
      setPreviewFile(null);
    }
  }, [fileIndex, selected]);

  const nextFile = () => {
    const nextIndex = (fileIndex + 1) % selected.files.length;
    const nextActive = parseInt(active);
    if (nextIndex === 0 && nextActive + 1 < bills.length) {
      setActive(nextActive + 1);
      setselected(bills[nextActive + 1]);
    }
    setFileIndex(nextIndex);
  };

  const previousFile = () => {
    const prevIndex =
      fileIndex === 0 ? selected.files.length - 1 : fileIndex - 1;
    const nextActive = parseInt(active);
    if (fileIndex === 0 && nextActive > 0) {
      setActive(nextActive - 1);
      setselected(bills[nextActive - 1]);
    }
    setFileIndex(prevIndex);
  };

  const BillCurrency = (type) => {
    const currency = CurrenciesList.find((currency) => currency.code === type);
    const symbol = currency ? currency.symbol : "";
    return symbol;
  };

  const getFileNameFromUrl = (url) => {
    if (!url || typeof url !== "string") return "Unknown File";
    const decoded = decodeURIComponent(url);
    return decoded.substring(decoded.lastIndexOf("/") + 1);
  };

  const getDisplayFileName = (file, index) => {
    let fileName = "Unknown File";

    if (selected?.fileNames && selected.fileNames[index]) {
      fileName = selected.fileNames[index];
    } else if (file instanceof File) {
      fileName = file.name;
    } else if (typeof file === "string") {
      fileName = getFileNameFromUrl(file);
    }

    const timestampPattern = /^.*GMT[+-]\d{4}\s*\([^)]+\)/;
    if (timestampPattern.test(fileName)) {
      fileName = fileName.replace(timestampPattern, "").trim();
    }

    return fileName || "Unknown File";
  };

  const getFileExtension = (url) => {
    if (!url) return "";
    return url.split(".").pop()?.toLowerCase() || "";
  };

  const isPreviewableFile = (url) => {
    const extension = getFileExtension(url);
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const pdfExtensions = ["pdf"];
    return [...imageExtensions, ...pdfExtensions].includes(extension);
  };

  const handleFileClick = (file) => {
    const resolvedUrl = view
      ? file
      : file instanceof File
      ? URL.createObjectURL(file)
      : file;
    if (isPreviewableFile(resolvedUrl)) {
      setPreviewFile(resolvedUrl);
      setPreviewVisible(true);
    } else {
      handleDownload(resolvedUrl);
    }
  };

  const handleDownload = async (url) => {
    try {
      const decodedURL = decodeURIComponent(url);
      const fileName = decodedURL.split("/").pop().split("?")[0];
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const renderPreviewContent = () => {
    if (!previewFile) return null;

    const extension = getFileExtension(previewFile);
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

    if (imageExtensions.includes(extension)) {
      return <PreviewImage src={previewFile} alt="Preview" />;
    } else if (extension === "pdf") {
      return (
        <PDFContainer>
          <iframe
            src={previewFile}
            title="PDF Preview"
            type="application/pdf"
          />
        </PDFContainer>
      );
    }
    return <div>Preview not available for this file type</div>;
  };

  return (
    <div>
      <Row>
        <ExpenseTitle>Expense</ExpenseTitle>
        {bills.map((data, index) => (
          <PageIndicator
            key={index}
            className={active === index ? "active" : ""}
            onClick={() => {
              setselected(data);
              setActive(index);
            }}
          >
            {index + 1}
          </PageIndicator>
        ))}
      </Row>
      <StyledContainer>
        <div>
          <span className="title">Name of Expense</span>
          <span className="value">{selected?.nameofexpense}</span>
        </div>
        <div>
          <span className="title">Total amount spent</span>
          <span className="value">
            {BillCurrency(selected?.amountType)}&nbsp;{selected?.totalAmount}
          </span>
        </div>
      </StyledContainer>
      <FileContainer>
        {(() => {
          const file = selected?.files[fileIndex];
          const fileName = selected?.fileNames[fileIndex];
          const resolvedUrl = view
            ? file
            : file instanceof File
            ? URL.createObjectURL(file)
            : file;

          const extension = fileName?.split(".")?.pop()?.toLowerCase();
          const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
            extension
          );

          return isImage ? (
            <img
              src={resolvedUrl}
              alt="bill"
              style={{ maxWidth: "100%", maxHeight: "100%" }}
              onClick={() => handleFileClick(file)}
            />
          ) : (
            <span onClick={() => handleFileClick(file)}>
              {getDisplayFileName(file, fileIndex)}
            </span>
          );
        })()}
      </FileContainer>

      <div className="d-flex align-items-center">
        <div className="me-auto">
          <Fileindex>{active + 1}</Fileindex> <Fileindex>-</Fileindex>{" "}
          <Fileindex>
            {fileIndex + 1}/{selected?.files?.length}
          </Fileindex>
        </div>
        <BtnContainer>
          <span className="next-btn" onClick={previousFile}>
            {"<"}
          </span>
          <span className="next-btn" onClick={nextFile}>
            {">"}
          </span>
        </BtnContainer>
      </div>
      <PreviewModal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="download" onClick={() => handleDownload(previewFile)}>
            Download
          </Button>,
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>,
        ]}
        width="90%"
        centered
        title="File Preview"
      >
        {renderPreviewContent()}
      </PreviewModal>
    </div>
  );
};

export default ViewBillProff;